import { useEffect, useId, useState } from 'react';

import {
  Accordion,
  Box,
  Button,
  ButtonGroup,
  FormField,
  Heading,
  Icon,
  Input,
  Label,
  Modal,
  Paragraph,
  Text,
  Textarea,
  TextButton,
  Toast,
  ToggleCard,
  useToast,
} from '@zillow/constellation-10';
import { IconLocationFilled, IconMapFilled } from '@zillow/constellation-icons';

import { useQueryClient } from '@zg-rentals/react-query5';
import { RentalManagerSupportHelpTopic } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserSupportWebApiHandler_submitRequest } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { useRentalManagerUserPropertyDetailsWebApiHandler_overview } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';
import {
  emitClickSubmitMapPinAdjustmentRequestEvent,
  emitViewMapPinAdjustmentModalFromPostingPathEvent,
} from '@zg-rentals/zillow-rental-manager/analytics/events/correctMapPin';
import { formatAddress } from '@zg-rentals/zillow-rental-manager/utils';

import { getListingDetailsQueryKey } from '../../../hooks/queries/properties';

import { useAuth } from '../../AuthProvider';

export const SUCCESS_TOAST_MESSAGE = 'Location adjustment request submitted';

type Props = {
  listingAlias: string;
  onClose: () => void;
  isFromPostingPath?: boolean;
};

const MapPinAdjustmentRequestModal = ({ listingAlias, onClose, isFromPostingPath = false }: Props) => {
  const verificationMethodHeadingId = useId();
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { mutate: submitRequest } = useRentalManagerUserSupportWebApiHandler_submitRequest();

  const { data: overviewData } = useRentalManagerUserPropertyDetailsWebApiHandler_overview(
    { listingAlias },
    { enabled: Boolean(listingAlias) },
  );

  const address = overviewData?.address ?? {};
  const email = user?.email ?? '';

  const [locationDescription, setLocationDescription] = useState('');
  const [activeId, setActiveId] = useState(-1);

  useEffect(() => {
    if (isFromPostingPath && user) {
      emitViewMapPinAdjustmentModalFromPostingPathEvent({ user });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderToggleCard = (id: number, icon: React.ReactNode, title: string, description: string) => (
    <ToggleCard
      selected={activeId === id}
      aria-label={title}
      onClick={() => handleToggleCardClick(id)}
      css={{ textAlign: 'center', flex: 1 }}
      data-testid={`MapPinAdjustmentRequestModal-ToggleCard-${id}`}
    >
      <Icon size="md" css={{ marginBottom: '200' }}>
        {icon}
      </Icon>
      <Paragraph textStyle="body-sm-bold">{title}</Paragraph>
      <Paragraph textStyle="body-xs" color="text.subtle">
        {description}
      </Paragraph>
    </ToggleCard>
  );

  const handleToggleCardClick = (id: number) => {
    setActiveId(id);
    setLocationDescription('');
  };

  const handleSubmit = () => {
    if (user) {
      const currentProperty = { listingAlias, ...address };
      emitClickSubmitMapPinAdjustmentRequestEvent({
        user,
        currentProperty,
        triggerLocation: isFromPostingPath ? 'posting_path' : 'zrm_property_overview',
      });
    }

    const {
      street = '',
      unit = '',
      city = '',
      state: addressState = '',
      zip = '',
    } = address as {
      street?: string;
      unit?: string;
      city?: string;
      state?: string;
      zip?: string;
    };

    submitRequest(
      {
        postData: {
          helpTopicKey: RentalManagerSupportHelpTopic.FIX_MAP_PIN,
          helpTopicString: 'Fix map pin',
          description: locationDescription,
          email,
          preferredEmail: '',
          listingAddressFreeForm: formatAddress({ street, unit, city, state: addressState, zip })
            .formattedCompleteAddress,
          aliases: listingAlias ? [listingAlias] : [],
          userTypeKey: '',
          userTypeString: '',
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_overview.getKey({ listingAlias }),
          });
          queryClient.invalidateQueries({ queryKey: getListingDetailsQueryKey(listingAlias) });
          enqueueToast(<Toast tone="success">{SUCCESS_TOAST_MESSAGE}</Toast>);
          onClose();
        },
        onError: (err: unknown) => {
          enqueueToast(
            <Toast tone="critical">
              {DatarouterError.getDisplayMessage(err) || 'Request couldn’t be submitted. Try again.'}
            </Toast>,
          );
        },
      },
    );
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      size="md"
      header={<Heading level={1}>Submit a location adjustment request</Heading>}
      body={
        <>
          <Heading id={verificationMethodHeadingId} level={2} textStyle="body-lg-bold" css={{ marginBottom: '300' }}>
            Select a verification method
          </Heading>
          <Paragraph>Select how you’d like to provide the correct location information</Paragraph>
          <Box
            role="group"
            aria-labelledby={verificationMethodHeadingId}
            css={{
              display: 'flex',
              gap: '600',
              justifyContent: 'center',
              alignItems: 'stretch',
              marginBlock: '400',
              marginInline: '900',
            }}
          >
            {renderToggleCard(0, <IconMapFilled />, 'Google Maps link', 'Share a link from Google Maps')}
            {renderToggleCard(1, <IconLocationFilled />, 'Latitude/Longitude', 'Provide exact numerical coordinates')}
          </Box>
          {activeId === 0 && (
            <FormField
              control={
                <Textarea
                  value={locationDescription}
                  placeholder="Paste the Google Maps link to your property here..."
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setLocationDescription(e.target.value)}
                />
              }
              label={<Label required={true}>Google Maps link</Label>}
              required={true}
            />
          )}
          {activeId === 1 && (
            <>
              <FormField
                control={
                  <Input
                    value={locationDescription}
                    placeholder="e.g. 34.0522° N, 118.2437° W"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocationDescription(e.target.value)}
                  />
                }
                label={<Label required={true}>Coordinates</Label>}
                required={true}
              />
              <Accordion.Root>
                <Accordion.Item value="MapPinAdjustmentRequestModal-HowToFindCoordinates" css={{ marginTop: '400' }}>
                  <Accordion.Header>
                    <Text textStyle="body-bold">How to find your coordinates</Text>
                  </Accordion.Header>
                  <Accordion.Panel>
                    <strong>Google Maps: </strong>Right-select your property location, select “What’s here?”, and copy
                    the coordinates from the popup.
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion.Root>
            </>
          )}
          <Paragraph textStyle="body-xs-bold" css={{ marginTop: '300', marginBottom: '100' }}>
            When can I expect a response?
          </Paragraph>
          <Paragraph textStyle="body-xs">
            We respond to requests during weekday business hours, <br />
            Monday - Friday from 8am PST - 4pm PST.
          </Paragraph>
        </>
      }
      dividers={true}
      footer={
        <ButtonGroup aria-label="modal actions">
          <TextButton onClick={onClose}>Cancel</TextButton>
          <Button emphasis="filled" tone="brand" onClick={handleSubmit} disabled={!locationDescription}>
            Submit
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default MapPinAdjustmentRequestModal;
